import {
  disposeRuntimeInstance,
  hasRuntimeInstance,
} from "../runtime/connectionRuntimeService";
import { deactivateRuntimeConnection } from "../runtime/runtimeConnectionService";
import { deleteStudioConnection } from "./connectionService";
import { clearConnectionLocalCaches } from "./connectionCleanupService";
import { saveConnectionDeleteHistory } from "./connectionHistoryService";

export class ConnectionDeleteError extends Error {
  constructor(message, { code = "delete_failed", connection = null } = {}) {
    super(message);
    this.name = "ConnectionDeleteError";
    this.code = code;
    this.connection = connection;
  }
}

export function isActiveConnection(connection, workspace) {
  if (!connection) return false;
  return Boolean(connection.active);
}

export function isWorkspaceConnection(connection, workspace) {
  if (!connection?.id) return false;
  return Boolean(workspace?.connectionId && workspace.connectionId === connection.id);
}

export function pickAlternateConnection(connections, deletedId, preferredProjectId) {
  const remaining = (connections || []).filter((c) => c.id !== deletedId && !c.archived);
  if (!remaining.length) return null;

  const sameProject = remaining.filter((c) => c.projectId === preferredProjectId);
  const pool = sameProject.length ? sameProject : remaining;
  return pool.find((c) => c.active) || pool.find((c) => c.favorite) || pool[0];
}

/**
 * Enterprise delete — Studio metadata only. Never deletes customer Firebase data.
 */
export async function enterpriseDeleteConnection({
  connection,
  deletedBy = "",
  reason = "",
  workspace = null,
  remainingConnections = [],
}) {
  if (!connection?.id) {
    throw new ConnectionDeleteError("Connection not found.");
  }

  if (isActiveConnection(connection, workspace)) {
    throw new ConnectionDeleteError(
      "This connection is marked Active. Deactivate it before deleting.",
      { code: "active_connection", connection }
    );
  }

  const connectionId = connection.id;
  const wasWorkspaceConnection = workspace?.connectionId === connectionId;

  try {
    clearConnectionLocalCaches(connectionId, { wasWorkspaceConnection });

    if (hasRuntimeInstance(connectionId)) {
      await disposeRuntimeInstance(connectionId);
    }

    if (wasWorkspaceConnection) {
      await deactivateRuntimeConnection(connectionId);
    }

    await deleteStudioConnection(connectionId);

    await saveConnectionDeleteHistory({
      connectionId,
      projectId: connection.projectId || "",
      deletedBy,
      reason,
      connectionName: connection.name || "",
      firebaseProjectId: connection.firebaseProjectId || "",
    });

    const nextConnection = pickAlternateConnection(
      remainingConnections,
      connectionId,
      connection.projectId
    );

    return {
      deletedId: connectionId,
      nextConnection,
      hadWorkspaceBinding: wasWorkspaceConnection,
    };
  } catch (err) {
    if (err instanceof ConnectionDeleteError) throw err;
    throw new ConnectionDeleteError(err?.message || "Connection deletion failed.", {
      code: "delete_failed",
      connection,
    });
  }
}

export async function enterpriseDeleteConnectionsBulk({
  connections = [],
  deletedBy = "",
  reason = "",
  workspace = null,
  remainingConnections = [],
}) {
  const deleted = [];
  let hadWorkspaceBinding = false;
  try {
    for (const connection of connections) {
      if (workspace?.connectionId === connection.id) hadWorkspaceBinding = true;
      const result = await enterpriseDeleteConnection({
        connection,
        deletedBy,
        reason,
        workspace,
        remainingConnections: remainingConnections.filter((c) => !deleted.includes(c.id)),
      });
      deleted.push(connection.id);
      if (result.hadWorkspaceBinding) hadWorkspaceBinding = true;
    }
    const lastRemaining = remainingConnections.filter((c) => !deleted.includes(c.id));
    const nextConnection = pickAlternateConnection(
      lastRemaining,
      deleted[deleted.length - 1] || "",
      connections[0]?.projectId
    );
    return { deletedIds: deleted, nextConnection, hadWorkspaceBinding };
  } catch (err) {
    const partial = deleted.length
      ? ` ${deleted.length} connection(s) were deleted before the failure.`
      : "";
    if (err instanceof ConnectionDeleteError) {
      throw new ConnectionDeleteError(`${err.message}${partial}`, {
        code: err.code,
        connection: err.connection,
      });
    }
    throw new ConnectionDeleteError(`${err?.message || "Bulk delete failed."}${partial}`, {
      code: "bulk_delete_failed",
    });
  }
}
